import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Ban,
  Check,
  Clock,
  DoorOpen,
  ExternalLink,
  Inbox,
  RefreshCw,
  Euro,
  FileText,
  Heading,
  ImageIcon,
  Sparkles,
  CalendarDays,
  Ticket,
  UserCheck,
  Users,
  X
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { SearchFilterForm } from "../../components/ui/SearchFilterForm";
import { CitySelect } from "../../components/ui/CitySelect";
import { Card, CardContent } from "../../components/ui/Card";
import { Pagination } from "../../components/ui/Pagination";
import { Badge } from "../../components/ui/Badge";
import { AutoAcceptedBadge } from "../../components/ui/AutoAcceptedBadge";
import Input from "../../components/ui/Input";
import { BillingRankInput } from "../../components/ui/BillingRankInput";
import { Select } from "../../components/ui/Select";
import { EventStatusBadge } from "../../components/ui/EventStatusBadge";
import { EntityLink } from "../../components/ui/EntityLink";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { ActionButton, AbsentSection } from "../../components/ui/ActionButton";
import ArtistMatchModal from "./ArtistMatchModal";
import UrlTargetModal, { occurrenceLabel } from "./UrlTargetModal";
import { artistsApi, eventResearchApi, eventsApi, imageReviewApi } from "../../utils/api";
import {
  apiErrorMessage,
  displayLabel,
  getEventWebUrl,
  normalizeImageRefs,
  parsePriceInput,
  parseRankInput,
  parseWholeNumberInput,
  rankIsUnusable,
  rankLabel,
  retryHint
} from "../../utils/helpers";
import { QueueSpinner, StatusTabs, SuccessToast } from "../../components/ui/ResearchQueue";
import { useResearchQueue } from "../../hooks/useResearchQueue";
import EventDisplayImage from "../events/EventDisplayImage";
import ImageSuggestionsPanel from "../events/ImageSuggestionsPanel";

// Small on purpose. Every card with an open image alert mounts an ImageSuggestionsPanel, which
// immediately reads up to six pages the event links to — so page size is a multiplier on
// outbound traffic to other people's servers, not just on rows. Most research records have an
// image alert in practice. Raise it only if that stops being true.
const PAGE_SIZE = 10;

/**
 * Every acceptable item shows one, because every item is accepted on its own — an admin deciding
 * about one support act should not have to infer its reliability from the record as a whole.
 * Banded rather than raw so the number reads as a recommendation: the agent's own accept floors
 * are 0.6 (line-up) and 0.7 (price), so below 0.6 is where it would not have acted by itself.
 */
function Confidence({ value }) {
  if (typeof value !== "number") return null;
  const variant = value >= 0.8 ? "success" : value >= 0.6 ? "secondary" : "warning";
  return (
    <Badge variant={variant} title="How sure the agent is about this item">
      {Math.round(value * 100)}%
    </Badge>
  );
}

/**
 * Who answered this item (#827), as the run stamped it: `claude:<model>` or `codex:<model>:<effort>`.
 * For information when comparing backends, so quiet text rather than a badge. Per item, because a
 * later pass may have merged into the record on the other backend; items from before the stamp
 * show nothing.
 */
function ResearchedBy({ value }) {
  if (!value) return null;
  const [backend, ...rest] = value.split(":");
  const name = { claude: "Claude", codex: "Codex" }[backend] || backend;
  return (
    <span className="whitespace-nowrap text-xs font-normal text-muted-foreground" title="Who answered this item">
      {[name, ...rest].join(" · ")}
    </span>
  );
}

// Mirrors CANDIDATE_CONFIDENCE_FLOOR in domains/reports/research_record.py. Below it the model's
// own reasoning has said things like "2025 date seen" or "403 on fetch" — worth keeping, not
// worth offering as an equal choice.
const CANDIDATE_CONFIDENCE_FLOOR = 0.7;

// Mirrors MAX_DESCRIPTION_CHARS in shared/constants/events.py, which both request schemas enforce
// (JS cannot import it). Without the cap here, pasting a long passage from a venue page returns a
// raw 422 validation blob instead of simply refusing the extra characters.
const MAX_DESCRIPTION_CHARS = 2000;

/**
 * Why a section has nothing in it.
 *
 * "No artists listed" and "we could not find out" and "this event has no performers by nature"
 * all rendered as the same blank space, which is the least useful thing the screen could say — an
 * admin then has to open the event to find out whether anything is owed. The run already recorded
 * the answer in its `lineup_check` / `price_check` markers; this reads them back out.
 */
function absentLineupReason(record) {
  const verdict = record.event_lineup_check?.verdict;
  if (verdict === "complete") return "Line-up already complete — a source showed the bill and nobody was missing.";
  if (verdict === "no_artists")
    return "No performer to name — billed by the work, the club night or the screening rather than by anyone appearing.";
  if (verdict === "no_result") return `Could not confirm the line-up${retryHint(record.event_lineup_check)}.`;
  if (verdict === "reported") return "Every artist it found has been accepted.";
  return "Line-up was not researched on this pass.";
}

function absentPriceReason(record) {
  const price = record.event_start_price;
  if (price?.price != null) return `Already priced — ${price.price} ${price.currency || ""}`.trim() + ".";
  const verdict = record.event_price_check?.verdict;
  if (verdict === "free") return "Admission is free — a source said so explicitly.";
  if (verdict === "no_result") return `No price found${retryHint(record.event_price_check)}.`;
  if (verdict === "reported") return "The price it found has been accepted.";
  return "Price was not researched on this pass.";
}

function absentTitleReason(record) {
  if (record.event_title_locked)
    return record.event_preferred_title
      ? `An admin set this event's title — ${record.event_preferred_title}.`
      : "An admin chose to show the provider's title.";
  const verdict = record.event_title_check?.verdict;
  if (verdict === "fine") return "The provider's title is fine as it is.";
  if (verdict === "no_result") return `Could not tell whether the title needs fixing${retryHint(record.event_title_check)}.`;
  if (verdict === "reported") return "The title it proposed has been accepted.";
  return "Title was not researched on this pass.";
}

function absentDescriptionReason(record) {
  if (record.event_ai_description) return "The event already carries an AI description.";
  const verdict = record.event_description_check?.verdict;
  if (verdict === "no_result")
    return `No source said enough about this event to describe it${retryHint(record.event_description_check)}.`;
  if (verdict === "reported") return "The description it wrote has been accepted.";
  return "Description was not researched on this pass.";
}


/**
 * Put a ticket URL on the event that the record does not propose.
 *
 * The agent is frequently one level off: it returns the venue's programme index, the provider's
 * city listing, or the artist's tour page rather than the page for this date. An admin who opens
 * a candidate to check it is already looking at the right link, and the only place to put it used
 * to be the events editor — which rewrites the whole `urls` array from a form, so fixing one link
 * risked every stored provider and `bad` marker on the event.
 *
 * Server-side this is the same http(s)/resale/duplicate path the candidate accept uses; only the
 * "must be one the agent listed" check is dropped. Nothing is stamped on the record, so adding a
 * URL here never auto-resolves it.
 */
function ManualUrl({ record, busy, act }) {
  const [url, setUrl] = useState("");
  const [picking, setPicking] = useState(false);
  const key = `manual-url:${record.id}`;
  const trimmed = url.trim();
  const onEvent = record.event_urls || [];
  // 2-5 dates only; see UrlTargetModal. An event outside that range never renders a picker, and
  // this whole section behaves exactly as it did before.
  const occurrences = record.event_occurrences || [];
  // A candidate the run auto-accepted is on the event now, so the open card hides it from the
  // proposals: this list is where it shows, and the badge is how it reads as automatic (#792).
  const autoAccepted = new Set(
    (record.ticket_candidates || []).filter((candidate) => candidate.auto_accepted).map((candidate) => candidate.url)
  );
  const add = (occurrenceId) =>
    act(
      key,
      async () => {
        const result = await eventResearchApi.addUrl(record.id, trimmed, occurrenceId);
        setUrl("");
        setPicking(false);
        // The server's message, which also says when a provider image rule added the event's image.
        return result?.message;
      },
      occurrenceId ? "URL added to that date" : "URL added to the event"
    );

  return (
    <div className="space-y-2 pt-1">
      {/* What the event carries right now, so an add is visible where it was made and a mistake
          can be undone without leaving for the events editor. Also the only place the links
          ingestion supplied are visible at all — the candidate rows above only cover what the
          agent proposed. */}
      {onEvent.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">On the event ({onEvent.length})</p>
          {onEvent.map((eventUrl) => (
            <div key={eventUrl} className="flex items-start gap-2">
              <a
                href={eventUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 break-all text-xs text-primary underline"
              >
                {eventUrl}
              </a>
              {autoAccepted.has(eventUrl) && <AutoAcceptedBadge />}
              <ActionButton
                busy={busy}
                actionKey={`remove-url:${record.id}:${eventUrl}`}
                variant="ghost"
                onClick={() =>
                  act(
                    `remove-url:${record.id}:${eventUrl}`,
                    () => eventResearchApi.removeUrl(record.id, eventUrl),
                    "URL removed from the event"
                  )
                }
              >
                Remove
              </ActionButton>
            </div>
          ))}
          {/* Said plainly because the two cases look identical in this list: a link the daily
              fetch emits is re-added on the next run, so removing it here is temporary. */}
          <p className="text-xs text-muted-foreground">
            Removing a link the daily fetch supplies is temporary — it returns on the next run. Mark it bad on the
            event instead.
          </p>
        </div>
      )}
      {/* One list per date, because that is where the links actually are on a multi-date event:
          `El Consorcio at Teatro Nuevo Alcala` carries nothing at event level and a different
          provider page on each of its two nights. Without this the section reported "no links"
          for an event that has four. */}
      {occurrences.map((occurrence) => (
        <div key={occurrence.id} className="space-y-1">
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            {occurrenceLabel(occurrence)} ({occurrence.urls.length})
          </p>
          {occurrence.urls.length === 0 && (
            <p className="text-xs text-muted-foreground">No link filed against this date.</p>
          )}
          {occurrence.urls.map((occurrenceUrl) => {
            const removeKey = `remove-url:${record.id}:${occurrence.id}:${occurrenceUrl}`;
            return (
              <div key={occurrenceUrl} className="flex items-start gap-2">
                <a
                  href={occurrenceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 break-all text-xs text-primary underline"
                >
                  {occurrenceUrl}
                </a>
                {autoAccepted.has(occurrenceUrl) && <AutoAcceptedBadge />}
                <ActionButton
                  busy={busy}
                  actionKey={removeKey}
                  variant="ghost"
                  onClick={() =>
                    act(
                      removeKey,
                      () => eventResearchApi.removeUrl(record.id, occurrenceUrl, occurrence.id),
                      "URL removed from that date"
                    )
                  }
                >
                  Remove
                </ActionButton>
              </div>
            );
          })}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="min-w-[18rem] flex-1"
          placeholder="https://venue.example/this-event"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <ActionButton
          busy={busy}
          actionKey={key}
          disabled={!trimmed}
          onClick={() => (occurrences.length ? setPicking(true) : add(null))}
        >
          Add URL
        </ActionButton>
      </div>
      {picking && (
        <UrlTargetModal
          open={picking}
          onOpenChange={setPicking}
          url={trimmed}
          occurrences={occurrences}
          busy={busy === key}
          onConfirm={add}
        />
      )}
    </div>
  );
}

/**
 * Set a starting price by hand, for the pass that could not find one.
 *
 * The agent regularly says in `notes` what it actually saw — "only the members' rate was
 * published", "the venue lists 15-25 EUR with no tier named for this date" — while still
 * answering `not_found`. That is usually enough for a human to settle the price, and until now
 * the answer had to be typed on the events screen, with the reasoning left behind on this one.
 *
 * Shown only while the record is open and the event has no price: the write is set-if-missing, so
 * offering the box for an already-priced event could only ever produce a 409.
 */
// Same three the event editor offers. Every configured city is in the euro zone today, so this is
// really "EUR, with an escape hatch" — but the two screens writing the same field should not
// disagree about what can be typed into it.
const CURRENCIES = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" }
];

function ManualPrice({ record, busy, act }) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const key = `manual-price:${record.id}`;
  // Same parser the proposal box uses: "22,50" must not reach the API as NaN, and null disables
  // the button rather than letting a blank or malformed amount be submitted.
  const parsed = parsePriceInput(amount);

  return (
    <section className="space-y-1">
      <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Euro className="h-4 w-4" /> Starting price
      </p>
      <p className="text-xs text-muted-foreground">{absentPriceReason(record)} Set it by hand if you know it.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="w-32" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
        {/* The shared Select, not a bare <select>: it hides the native arrow (`appearance-none`)
            and draws its own with room reserved for it. A raw one sized to its content renders
            the platform arrow flush against the text, which reads as a stray glyph after "EUR". */}
        <Select
          className="w-28"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          options={CURRENCIES}
          // Never selected — a currency always is — but Select prepends a disabled placeholder
          // unless an option carries value "", and giving it one would make an empty currency
          // pickable. So it only ever needs to not be the component's Spanish default.
          placeholder="Currency"
        />
        <ActionButton
          busy={busy}
          actionKey={key}
          disabled={parsed === null}
          onClick={() =>
            act(key, () => eventResearchApi.setPrice(record.id, parsed, currency), "Price saved to the event")
          }
        >
          Save price
        </ActionButton>
      </div>
    </section>
  );
}

/**
 * One screen per event: everything the research agent found, accepted item by item.
 *
 * Reads `event_research` records — not reports. Each record is one agent pass over one event
 * (ticket candidates, missing artists, proposed price), and each item carries its own acceptance
 * stamp, so an admin can take two artists, skip the price and add one URL and have the record
 * remember exactly that. The record auto-resolves once nothing acceptable is left; the Dismiss and
 * Done buttons are the escape hatch when the findings are wrong or not worth taking.
 */
export default function EventResearchPage() {
  const [city, setCity] = useState("");
  const {
    records,
    setRecords,
    pagination,
    status,
    page,
    setPage,
    searchInput,
    setSearchInput,
    search,
    loading,
    error,
    setError,
    success,
    setSuccess,
    busy,
    setBusy,
    load,
    act,
    resetPage,
    changeStatus,
    runSearch,
    resetSearch
  } = useResearchQueue({
    api: eventResearchApi,
    pageSize: PAGE_SIZE,
    entityLabel: "event",
    filters: city ? { city } : {}
  });

  const changeCity = (value) => {
    setCity(value);
    resetPage();
  };

  // Settling the image alert updates the card in place and does NOT re-query. It is the one
  // action that changes the server's own sort key (`has_open_image_review` ranks first), so a
  // refresh here re-ranks the card being worked on — down the page, and past enough remaining
  // alerts, onto another page entirely. Losing the record mid-edit is far worse than a card
  // showing slightly stale sibling data until the next real load.
  const settleImageAlert = useCallback(
    (recordId, message) => {
      setSuccess(message);
      setRecords((current) =>
        current.map((record) =>
          record.id === recordId ? { ...record, has_open_image_review: false, event_suspicious_image_urls: [] } : record
        )
      );
    },
    [setSuccess, setRecords]
  );

  if (loading) return <QueueSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sparkles className="h-5 w-5" /> Event research
          </h1>
          <p className="text-sm text-muted-foreground">
            Everything the agent found about one event, in one pass.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={Boolean(busy)}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusTabs status={status} onChange={changeStatus} />
        <SearchFilterForm
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={runSearch}
          onClear={resetSearch}
          placeholder="Search title or event ID"
          loading={loading}
        />
        <CitySelect value={city} onChange={changeCity} />
        <span className="text-sm text-muted-foreground">{pagination.total} record(s)</span>
      </div>

      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <SuccessToast message={success} />

      {records.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            <Inbox className="h-8 w-8" />
            <p>
              {search || city
                ? "No research records match this filter."
                : `No ${status} research records.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        records.map((record) => (
          <RecordCard
            key={record.id}
            record={record}
            busy={busy}
            setBusy={setBusy}
            act={act}
            onImageSettled={settleImageAlert}
            onError={setError}
          />
        ))
      )}

      <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
    </div>
  );
}

/**
 * The event's image alert, settled from this same screen.
 *
 * Everything visual is the existing image-review machinery: `EventDisplayImage` renders exactly
 * what a consumer sees on desktop (same resolver as the events list and the review queue, so the
 * two screens cannot disagree about which image is live), and `ImageSuggestionsPanel` finds and
 * applies replacements — including its prepend-and-drop-the-flagged-one rule, which is not worth
 * a second implementation.
 *
 * The panel runs with `showSearch={false}`: only images already published on pages this event
 * links to, auto-loaded, no Brave. Those are free to read and on-topic by construction. Anything
 * wider stays in the full image review, which is one click away in the header.
 */
function ImageAlertSection({ record, busy, setBusy, pendingImages, setPendingImages, commit, onSettled, onError }) {
  const suspicious = record.event_suspicious_image_urls || [];
  const current = normalizeImageRefs(record.event_images || []);
  const images = pendingImages ?? current;
  const key = `images:${record.id}`;
  const changed = pendingImages !== null;

  // No refetch here, deliberately: settling the alert changes the server's own sort key, so
  // re-reading would move the record out from under the admin. The card is patched instead —
  // `has_open_image_review: false`, which is all it takes, since this section is the only place
  // the alert's own fields are rendered and it unmounts on that flag.
  const run = async (action, message) => {
    if (busy) return;
    setBusy(key);
    onError("");
    try {
      await action();
      onSettled(message);
    } catch (err) {
      console.error("Error resolving the image alert:", err);
      onError(apiErrorMessage(err, "Could not resolve the image alert"));
    } finally {
      setBusy(null);
    }
  };

  const saveAndResolve = () => run(commit, "Images updated and the alert resolved");

  return (
    <section className="space-y-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <ImageIcon className="h-4 w-4" /> Image alert
        <Badge variant="warning">needs review</Badge>
      </p>

      <div className="flex flex-wrap items-start gap-3">
        <figure className="w-40">
          <EventDisplayImage
            src={changed ? images[0]?.url : record.event_display_image?.url}
            alt={record.event_title || "Event image"}
            imageResolutionStatus={changed ? null : record.event_image_resolution_status}
            className="h-24 w-40 rounded border"
            compactLabel
          />
          <figcaption className="mt-1 text-xs text-muted-foreground">
            {changed ? "Will be shown after saving" : "Shown now on desktop"}
          </figcaption>
        </figure>
        <div className="min-w-[16rem] flex-1">
          {/* Discovery reads the pages the event links to, and accepting a ticket URL is exactly
              when a rescan is wanted — the page just added is usually the one carrying the poster.
              The panel scans once per eventId otherwise, which was invisible while every accept
              reloaded the page, and stopped being invisible once refreshes went silent. */}
          <ImageSuggestionsPanel
            rescanKey={(record.event_urls || []).join("|")}
            eventId={record.event_id}
            currentImages={images}
            suspiciousUrls={suspicious}
            onChange={setPendingImages}
            showSearch={false}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={Boolean(busy)}
          onClick={() => run(() => imageReviewApi.dismiss(record.event_id), "Image alert dismissed")}
        >
          Dismiss alert
        </Button>
        <ActionButton busy={busy} actionKey={key} disabled={!changed} onClick={saveAndResolve}>
          Save &amp; resolve
        </ActionButton>
      </div>
    </section>
  );
}

/**
 * One proposed ticket URL.
 *
 * Shows the agent's `reasoning` verbatim. That sentence is where it says "2025 date seen" or
 * "403 on fetch" — the thing that makes an otherwise plausible-looking link obviously wrong, and
 * which no amount of staring at the URL would reveal.
 */
function Candidate({ candidate, record, busy, act, isOpen, onEvent }) {
  const key = `url:${record.id}:${candidate.url}`;
  // Empty unless the event has 2-5 dates. With one date there is nothing to choose; with a long
  // run the dates are not sold separately anyway, so the accept stays a single click.
  const occurrences = record.event_occurrences || [];
  const [picking, setPicking] = useState(false);
  const add = (occurrenceId) =>
    act(
      key,
      async () => {
        const result = await eventResearchApi.acceptUrl(record.id, candidate.url, occurrenceId);
        setPicking(false);
        // The server's message, which also says when a provider image rule added the event's image.
        return result?.message;
      },
      occurrenceId ? "URL added to that date" : "URL added to the event"
    );
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <a
          href={candidate.url}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all text-sm text-primary underline"
        >
          {candidate.url}
        </a>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Confidence value={candidate.confidence} />
          <ResearchedBy value={candidate.researched_by} />
          {candidate.provider && <span>{candidate.provider}</span>}
          {/* Which night this page sells, when the agent could tell. Shown even on a record whose
              event has since dropped below two dates: it is what the agent claimed, and the
              picker (which is what would act on it) is already gone in that case. */}
          {candidate.date && (
            <Badge variant="outline">
              <CalendarDays className="mr-1 h-3 w-3" />
              {candidate.date}
            </Badge>
          )}
        </div>
        {candidate.reasoning && (
          <p className="mt-0.5 text-xs italic text-muted-foreground">{candidate.reasoning}</p>
        )}
      </div>
      {/* Nothing to add when the event already carries it. Offering the button anyway asks the
          admin to decide something that is already settled, and accepting it is a no-op the
          server reports as success — so the row looked actionable and did nothing. `onEvent`
          covers a URL that arrived any other way (ingestion, a hand edit); `accepted_at` is
          this record's own stamp.
          On an OPEN record neither fires: RecordCard drops those candidates entirely, since the
          "On the event" list already shows them. This is what a closed record renders, where
          that list is absent and the labels are the only record of what was taken.
          The stamp alone does NOT settle a multi-showtime event: filing this page against the
          30th stamps the candidate while the 1st still has nothing, so the button has to survive
          its own first accept. `onEvent` is the real test there — it means every showtime (or the
          event) carries it — and the picker greys out the showtimes that already do. */}
      {onEvent || (candidate.accepted_at && !occurrences.length) ? (
        <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
          <Check className="h-3 w-3" /> {candidate.accepted_at ? "Added" : "On the event"}
          {candidate.auto_accepted && <AutoAcceptedBadge />}
        </span>
      ) : (
        isOpen && (
          <>
            <ActionButton
              busy={busy}
              actionKey={key}
              className="shrink-0"
              onClick={() => (occurrences.length ? setPicking(true) : add(null))}
            >
              {occurrences.length ? "Add…" : "Add to event"}
            </ActionButton>
            {picking && (
              <UrlTargetModal
                open={picking}
                onOpenChange={setPicking}
                url={candidate.url}
                occurrences={occurrences}
                preselectDate={candidate.date}
                busy={busy === key}
                onConfirm={add}
              />
            )}
          </>
        )
      )}
    </div>
  );
}

/**
 * The billing position actually written to the event, for an accepted entry.
 *
 * Not `artist.performance_rank` — that is what the agent proposed, and the admin may have
 * corrected or cleared it on the way past. The accept stamps what it wrote, so the row can show
 * the rank the event carries rather than the one the row suggested. Records stamped before the
 * accept recorded it have no such key, and there the proposal is the best guess available.
 */
function acceptedRank(artist) {
  const stamp = artist.accepted;
  if (stamp && "performance_rank" in stamp) return stamp.performance_rank;
  return artist.performance_rank ?? null;
}

/**
 * One proposed artist, with the domain it inherited from the event.
 *
 * The domain is shown as removable chips rather than a fixed label: an artist can appear on an
 * event outside their usual domain (a musician in a theatre programme), and a wrong domain on a
 * newly created artist is worse than none — it is inherited, not observed. Whatever is left when
 * Accept is pressed is what the artist is created with; the server only accepts values the record
 * proposed, so this cannot invent a classification.
 */
function ProposedArtist({ artist, record, busy, setBusy, act, isOpen, onError }) {
  const [domain, setDomain] = useState(artist.domain || []);
  // The proposed billing position, editable before Accept. The agent reads it off a poster or a
  // venue page, where support acts are often listed in whatever order fits the layout — so it is
  // the one part of the proposal an admin routinely knows better. Blank = unranked, which is what
  // the server stores when it is null.
  const [rank, setRank] = useState(artist.performance_rank ?? "");
  const [matches, setMatches] = useState(null);
  const [matchTotal, setMatchTotal] = useState(0);
  const [checking, setChecking] = useState(false);
  const key = `artist:${record.id}:${artist.name}`;
  const canEdit = isOpen && !artist.accepted;
  // `min="1"`/`step="1"` enforce nothing on their own: there is no <form> here and Accept is not
  // a submit button, so the browser never runs constraint validation. Without this the box could
  // hold "0" and the accept would send null — adding the artist unranked, under a success toast,
  // with the typed value still on screen.
  const rankUnusable = rankIsUnusable(rank);

  const accept = async (artistId = null) => {
    // Also guarded on the button, but the modal's own Attach/Create call in here directly, and
    // the box stays editable behind it.
    if (rankUnusable) {
      onError("Billing position must be a whole number of 1 or more, or empty for unranked");
      return;
    }
    const before = record.id;
    await act(
      key,
      () => eventResearchApi.acceptArtist(record.id, artist.name, parseRankInput(rank), domain, artistId),
      artistId ? `Attached ${artist.name} to an existing artist` : `Added ${artist.name}`
    );
    // Only on success. `act` swallows its own errors and returns early when another action holds
    // the lock, so closing unconditionally threw the match list away on paths where nothing was
    // accepted — leaving the admin to re-run the check to get back to where they were.
    if (before === record.id && !artist.accepted) setMatches(null);
  };

  // Look before creating. Nothing dedups an accepted artist automatically anymore, so this is the
  // one moment a near-duplicate can be prevented rather than found in review afterwards. No
  // matches means straight through — a modal that always appears is a modal admins click past.
  const acceptChecked = async () => {
    // Take the page-wide lock for the CHECK too, not just the accept. `act` refuses to run while
    // anything else is in flight, and it refuses silently — so if another action started during
    // this round trip, the modal would open with its buttons live and the click would be dropped
    // with no request, no error and no spinner, closing as though it had worked.
    if (busy) return;
    setBusy(key);
    setChecking(true);
    let found;
    try {
      const result = await artistsApi.nameMatches(artist.name);
      found = result?.matches || [];
      // The list is capped by the endpoint's ?limit; the total is not. The modal says which it is
      // showing, so it cannot claim seven matches and render five without explaining the gap.
      setMatchTotal(result?.total ?? found.length);
    } catch (err) {
      // Must NOT fall through to creating. A swallowed failure here is indistinguishable from
      // "no duplicates" and quietly produces the exact artist this check exists to prevent —
      // which is what a mis-ordered route did once already, silently, with no sign anything
      // had gone wrong. Say so and let the admin retry instead.
      console.error("Error checking for existing artists:", err);
      onError(`Could not check whether “${artist.name}” already exists — nothing was added`);
      return;
    } finally {
      setChecking(false);
      setBusy(null);
    }
    if (found.length > 0) {
      setMatches(found);
      return;
    }
    accept();
  };

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">{artist.name}</span>
          {/* Before anything is clicked: which entries will need a decision. The modal only
              appears on Accept, which is too late to plan the pass. */}
          {artist.possible_duplicates > 0 && (
            <Badge variant="secondary" title="Artists already in Applauss with almost this name">
              {artist.possible_duplicates} possible duplicate
              {artist.possible_duplicates === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Confidence value={artist.confidence} />
          <ResearchedBy value={artist.researched_by} />
          {canEdit ? (
            <BillingRankInput value={rank} onChange={setRank} name={artist.name} />
          ) : rankLabel(artist.accepted ? acceptedRank(artist) : artist.performance_rank) ? (
            <span>{rankLabel(artist.accepted ? acceptedRank(artist) : artist.performance_rank)}</span>
          ) : null}
          {domain.map((value) => (
            <Badge key={value} variant="secondary" className="gap-1">
              {value}
              {canEdit && (
                <button
                  type="button"
                  title={`Do not give ${artist.name} the ${value} domain`}
                  aria-label={`Remove the ${value} domain`}
                  className="hover:text-foreground"
                  onClick={() => setDomain(domain.filter((d) => d !== value))}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      </div>
      {artist.accepted ? (
        <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
          <Check className="h-3 w-3" /> {artist.accepted.action || "added"}
          {artist.auto_accepted && <AutoAcceptedBadge />}
        </span>
      ) : (
        isOpen && (
          <>
            <ActionButton
              busy={busy || (checking ? key : false)}
              actionKey={key}
              disabled={rankUnusable}
              onClick={acceptChecked}
            >
              Accept
            </ActionButton>
            {matches && (
              <ArtistMatchModal
                open
                onOpenChange={(next) => !next && setMatches(null)}
                proposedName={artist.name}
                matches={matches}
                total={matchTotal}
                busy={busy === key}
                onAttach={(match) => accept(match.id)}
                onCreate={() => accept()}
              />
            )}
          </>
        )
      )}
    </div>
  );
}

/**
 * One artist the event already carries, with its billing position editable in place.
 *
 * The proposals above are by definition "artists the event does not list", so without this the
 * screen never showed the line-up it is adding to — and the one field an admin routinely knows
 * better than the source could only be corrected on the way in, never afterwards. Clearing it is
 * the same answer as leaving a proposal's box empty: the billing order is not known.
 *
 * The rank is editable, and the act can be hidden on this event — the events page's soft delete
 * (`POST /admin/events/{id}/artists/{artist_id}/hide`): it leaves the line-up, the event keeps a
 * record of it, and a re-fetch does not bring it back. Restoring it is on the event detail.
 * Renaming or merging the artist have their own screens.
 */
function EventArtist({ artist, record, busy, act, isOpen }) {
  const [rank, setRank] = useState(artist.performance_rank ?? "");
  const key = `event-artist:${record.id}:${artist.id}`;
  const stored = artist.performance_rank ?? null;
  const parsed = parseRankInput(rank);
  // Nothing to save until the box says something different from the event. Without this the Save
  // sits there on every row inviting a write that changes nothing — and each one is an event
  // update, a reindex, and a refresh of the card being worked on.
  const changed = !rankIsUnusable(rank) && parsed !== stored;
  const label = displayLabel(artist.preferred_name, artist.name);

  const save = () =>
    act(
      key,
      () => eventsApi.setArtistRank(record.event_id, artist.id, parsed),
      parsed === null
        ? `Cleared the billing position for ${label}`
        : `${label} is now ${rankLabel(parsed)}`
    );

  const hideKey = `${key}:hide`;
  const hide = () => {
    if (!window.confirm(`Hide ${label} on this event? It can be restored from the event, and a re-fetch will not bring it back.`)) return;
    act(hideKey, () => eventsApi.hideArtist(record.event_id, artist.id), `Hid ${label} on the event`);
  };

  return (
    <div className="flex items-start gap-2">
      <div className="flex flex-1 flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <EntityLink type="artist" id={artist.id} className="text-sm text-primary">
          {label}
        </EntityLink>
        {isOpen ? (
          <BillingRankInput value={rank} onChange={setRank} name={label} />
        ) : (
          <span>{rankLabel(stored) || "unranked"}</span>
        )}
      </div>
      {isOpen && changed && (
        <ActionButton busy={busy} actionKey={key} onClick={save}>
          Save
        </ActionButton>
      )}
      {isOpen && (
        <ActionButton busy={busy} actionKey={hideKey} onClick={hide} variant="ghost" aria-label={`Hide ${label} on this event`}>
          Hide
        </ActionButton>
      )}
    </div>
  );
}

/**
 * The description the pass wrote, next to the one the event was ingested with.
 *
 * Both are shown because the review is a comparison: the one thing this proposal is judged on is
 * that nothing in it was invented, and an admin cannot check that against a source they cannot
 * see. The ingested text is the source in the common case — the rule tells the model to rewrite
 * it rather than extend it — and the evidence links cover the rest.
 *
 * Editable before accepting, and that is the point rather than a concession. The usual defect is
 * one sentence no source supported; striking it is a smaller action than rejecting the whole
 * description and leaving the event with the marketing copy.
 */
function ProposedDescription({ record, busy, act, isOpen }) {
  const proposal = record.description;
  const [text, setText] = useState(proposal?.text || "");
  const key = `description:${record.id}`;
  const accepted = Boolean(proposal?.accepted_at);
  const edited = text.trim() !== (proposal?.text || "").trim();

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-medium">
        <FileText className="h-4 w-4" /> Description
        <Confidence value={proposal.confidence} />
        <ResearchedBy value={proposal.researched_by} />
        {edited && !accepted && <span className="text-xs font-normal text-muted-foreground">edited</span>}
      </p>
      <textarea
        value={text}
        disabled={!isOpen || accepted}
        rows={4}
        maxLength={MAX_DESCRIPTION_CHARS}
        onChange={(e) => setText(e.target.value)}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-70"
      />
      {proposal.notes && <p className="text-xs text-muted-foreground">{proposal.notes}</p>}
      {(record.event_description || (proposal.evidence_urls || []).length > 0) && (
        <details className="text-xs">
          <summary className="w-fit cursor-pointer text-muted-foreground hover:text-foreground">
            What it was written from
          </summary>
          <div className="mt-2 space-y-2">
            {record.event_description && (
              <p className="whitespace-pre-wrap text-muted-foreground">{record.event_description}</p>
            )}
            {(proposal.evidence_urls || []).map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block break-all text-primary underline"
              >
                {url}
              </a>
            ))}
          </div>
        </details>
      )}
      {accepted ? (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Check className="h-3 w-3" /> Saved to the event
          {proposal.auto_accepted && <AutoAcceptedBadge />}
        </span>
      ) : (
        isOpen && (
          <ActionButton
            busy={busy}
            actionKey={key}
            disabled={!text.trim()}
            onClick={() =>
              act(key, () => eventResearchApi.acceptDescription(record.id, text), "Description saved to the event")
            }
          >
            Accept description
          </ActionButton>
        )
      )}
    </section>
  );
}

// Mirrors MAX_TITLE_CHARS in apps/api/shared/constants/events.py; the API refuses a longer title.
const MAX_TITLE_CHARS = 200;

/**
 * A corrected title for an event whose provider title is broken (#814): shouting capitals, a ticket
 * tier, the venue glued on. Shown against the provider's title, editable before accepting like the
 * description; accepting writes `preferred_title` and locks it against re-fetch.
 */
function ProposedTitle({ record, busy, act, isOpen }) {
  const proposal = record.title;
  const [title, setTitle] = useState(proposal.title || "");
  const key = `title:${record.id}`;
  const accepted = Boolean(proposal.accepted_at);
  const edited = title.trim() !== (proposal.title || "").trim();

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-medium">
        <Heading className="h-4 w-4" /> Title
        <Confidence value={proposal.confidence} />
        <ResearchedBy value={proposal.researched_by} />
        {edited && !accepted && <span className="text-xs font-normal text-muted-foreground">edited</span>}
      </p>
      {/* What the agent is claiming, so the admin knows what to check: a rename rests on the page
          below being this event's own. A record from before renames carries no kind: a fix. */}
      <p className="text-xs text-muted-foreground">
        {proposal.kind === "renamed" ? "Named differently on the event's own page" : "Fixes a broken title"}
      </p>
      {/* Against the provider's title consumers see now: its unlocked preferred_title, which accepting
          replaces — never an admin's locked one, which is not the provider's (and refuses the accept). */}
      <ProviderLabel
        preferred={title}
        original={(!record.event_title_locked && record.event_preferred_title) || record.event_provider_title}
      />
      <Input
        value={title}
        disabled={!isOpen || accepted}
        maxLength={MAX_TITLE_CHARS}
        aria-label="Proposed title"
        onChange={(e) => setTitle(e.target.value)}
      />
      {proposal.notes && <p className="text-xs text-muted-foreground">{proposal.notes}</p>}
      {(proposal.evidence_urls || []).map((url) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block break-all text-xs text-primary underline"
        >
          {url}
        </a>
      ))}
      {accepted ? (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Check className="h-3 w-3" /> Saved to the event
          {proposal.auto_accepted && <AutoAcceptedBadge />}
        </span>
      ) : (
        isOpen && (
          <ActionButton
            busy={busy}
            actionKey={key}
            disabled={!title.trim()}
            onClick={() => act(key, () => eventResearchApi.acceptTitle(record.id, title), "Title saved to the event")}
          >
            Accept title
          </ActionButton>
        )
      )}
    </section>
  );
}

/**
 * The two stated values (#792): one integer a source states or does not. One table, so the run
 * time and the age read, accept and explain themselves the same way. `held` is what the event
 * already carries (the age is its own `min_age`, else the classifier's); the run's marker is
 * `event_<ask>_check` and the accept route `accept-<ask>`, both derived from `ask`.
 */
const STATED_VALUES = [
  {
    ask: "duration",
    key: "minutes",
    title: "Run time",
    unit: "min",
    icon: Clock,
    max: 1440,
    held: (record) => record.event_duration_minutes,
  },
  {
    ask: "min_age",
    key: "age",
    title: "Minimum age",
    unit: "+",
    icon: UserCheck,
    max: 99,
    held: (record) => record.event_min_age,
  },
];

function formatStatedValue(spec, value) {
  // An age of 0 is the classifier's "all ages": stated, but no minimum.
  if (spec.unit === "+") return value === 0 ? "all ages" : `${value}+`;
  return `${value} ${spec.unit}`;
}

function absentStatedValueReason(record, spec) {
  const held = spec.held(record);
  if (held != null) return `The event already has it — ${formatStatedValue(spec, held)}.`;
  const check = record[`event_${spec.ask}_check`];
  if (check?.verdict === "not_stated") return "No page it read states one.";
  if (check?.verdict === "not_yet_stated") return `Not published yet${retryHint(check)}.`;
  if (check?.verdict === "no_result") return `Could not confirm one${retryHint(check)}.`;
  // Not "accepted": the event holds no value (checked above), so an earlier record's proposal
  // was dismissed or is still waiting there.
  if (check?.verdict === "reported") return "An earlier pass proposed one; it is not on the event.";
  return `${spec.title} was not researched on this pass.`;
}

/**
 * The doors time and the artists' set times (#792): a list of times per date, accepted as a whole
 * onto the event's live dates. `has` is whether some date already holds one, which settles the ask.
 */
const TIME_ITEMS = [
  { ask: "doors", title: "Doors", icon: DoorOpen, has: (record) => record.event_has_doors },
  { ask: "set_times", title: "Set times", icon: Clock, has: (record) => record.event_has_set_times },
];

function absentTimesReason(record, spec) {
  if (spec.has(record)) return "The event already has them on at least one date.";
  const check = record[`event_${spec.ask}_check`];
  if (check?.verdict === "not_stated") return "No page it read states them.";
  if (check?.verdict === "no_result") return `Could not confirm them${retryHint(check)}.`;
  if (check?.verdict === "reported") return "An earlier pass proposed them; they are not on the event.";
  if (spec.ask === "set_times" && !(record.event_artists || []).length) return "The event lists no artists to time.";
  return `${spec.title} were not researched on this pass.`;
}

// A set time names the artist as the event stores it; the line-up's curated name wins (`displayLabel`).
function timedArtistLabel(record, entry) {
  const artist = (record.event_artists || []).find((a) => a.id === entry.artist_id);
  return displayLabel(artist?.preferred_name, artist?.name || entry.artist);
}

/** The proposed times, read-only, with one Accept for every date at once. */
function ProposedTimes({ record, spec, busy, act, isOpen }) {
  const proposal = record[spec.ask];
  const actionKey = `${spec.ask}:${record.id}`;
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">{spec.title}</p>
        <Confidence value={proposal.confidence} />
        <ResearchedBy value={proposal.researched_by} />
        {proposal.accepted_at ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" /> Saved
            {proposal.auto_accepted && <AutoAcceptedBadge />}
          </span>
        ) : (
          isOpen && (
            <ActionButton
              busy={busy}
              actionKey={actionKey}
              onClick={() =>
                act(
                  actionKey,
                  // The API's own message: it says when a date kept a different time, or when what
                  // landed could not be confirmed, which a fixed "saved" would hide.
                  async () => (await eventResearchApi.acceptTimes(record.id, spec.ask))?.message,
                  `${spec.title} saved to the event`
                )
              }
            >
              Accept
            </ActionButton>
          )
        )}
      </div>
      <ul className="space-y-0.5 text-sm">
        {(proposal.times || []).map((entry) => (
          <li key={`${entry.artist_id || ""}:${entry.date || ""}`}>
            {entry.artist && <span className="font-medium">{timedArtistLabel(record, entry)} · </span>}
            <span className="text-muted-foreground">{entry.date || "Every date"}</span> · {entry.time}
          </li>
        ))}
      </ul>
      {proposal.notes && <p className="text-xs text-muted-foreground">{proposal.notes}</p>}
      {(proposal.evidence_urls || []).map((url) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block break-all text-xs text-primary underline"
        >
          {url}
        </a>
      ))}
    </section>
  );
}

/**
 * A cancellation a page states for this event. Shown only when proposed: "still on" is the usual
 * answer and would put the same line on every card. Accepting marks the dates cancelled, which takes
 * them off every feed; ingestion keeps them cancelled, and the events editor is where one is undone.
 */
function ProposedCancellation({ record, busy, act, isOpen }) {
  const proposal = record.cancelled;
  const actionKey = `cancelled:${record.id}`;
  const dates = proposal.dates || [];
  return (
    <section className="space-y-2 rounded-md border border-destructive/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-destructive">
          <Ban className="h-4 w-4" /> Cancelled
        </p>
        <Confidence value={proposal.confidence} />
        <ResearchedBy value={proposal.researched_by} />
        {proposal.accepted_at ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" /> Dates cancelled
            {proposal.auto_accepted && <AutoAcceptedBadge />}
          </span>
        ) : (
          isOpen && (
            <ActionButton
              busy={busy}
              actionKey={actionKey}
              onClick={() =>
                act(
                  actionKey,
                  async () => (await eventResearchApi.acceptCancelled(record.id))?.message,
                  "Dates marked cancelled"
                )
              }
            >
              Mark cancelled
            </ActionButton>
          )
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {dates.length
          ? `On ${dates.join(", ")}`
          : record.event_live_nights > 1
            ? `Every upcoming date (${record.event_live_nights})`
            : "Every upcoming date"}
      </p>
      {proposal.notes && <p className="text-xs text-muted-foreground">{proposal.notes}</p>}
      {(proposal.evidence_urls || []).map((url) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block break-all text-xs text-primary underline"
        >
          {url}
        </a>
      ))}
    </section>
  );
}

/** An editable proposed value with an Accept button — `ProposedPrice`'s shape, for one integer. */
function ProposedStatedValue({ record, spec, busy, act, isOpen }) {
  const proposal = record[spec.ask];
  const [draft, setDraft] = useState(String(proposal[spec.key] ?? ""));
  const actionKey = `${spec.ask}:${record.id}`;
  const held = spec.held(record);
  // The events editor's parser: digits only within 1..max, so "1e2" or "0x5A" is refused rather
  // than accepted as 100 or 90, and blank accepts nothing.
  const { value } = parseWholeNumberInput(draft, spec.title, spec.max);
  const valid = value != null;

  return (
    <section className="space-y-2">
      <p className="text-sm font-medium">{spec.title}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-24"
          aria-label={spec.title}
          disabled={!isOpen || Boolean(proposal.accepted_at)}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <span className="text-sm text-muted-foreground">{spec.unit}</span>
        <Confidence value={proposal.confidence} />
        <ResearchedBy value={proposal.researched_by} />
        {proposal.accepted_at ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" /> Saved
            {proposal.auto_accepted && <AutoAcceptedBadge />}
          </span>
        ) : (
          isOpen && (
            <ActionButton
              busy={busy}
              actionKey={actionKey}
              disabled={!valid}
              onClick={() =>
                act(
                  actionKey,
                  () => eventResearchApi.acceptStatedValue(record.id, spec.ask, spec.key, value),
                  `${spec.title} saved to the event`
                )
              }
            >
              Accept
            </ActionButton>
          )
        )}
      </div>
      {/* Against what Accept would send, not the proposal: a different held value (the age counts
          the classifier's) is refused with a 409. `> 0`, since an "all ages" 0 holds no minimum. */}
      {held > 0 && valid && held !== value && !proposal.accepted_at && (
        <p className="text-xs text-amber-700">
          The event already has {formatStatedValue(spec, held)}; change it in the events editor instead.
        </p>
      )}
      {proposal.notes && <p className="text-xs text-muted-foreground">{proposal.notes}</p>}
      {(proposal.evidence_urls || []).map((url) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block break-all text-xs text-primary underline"
        >
          {url}
        </a>
      ))}
    </section>
  );
}

/**
 * The starting price the pass proposed, editable before it lands.
 *
 * Its own component with its own state, exactly like `ProposedDescription` — the edit used to
 * live in a page-level map threaded through `RecordCard`, which was a second mechanism for the
 * identical job and never resynced when a later pass replaced the proposal.
 *
 * Remounted by its caller when the proposed amount changes, which is what makes it resync.
 */
function ProposedPrice({ record, price, busy, act, isOpen }) {
  const [amount, setAmount] = useState(String(price.price ?? ""));
  const key = `price:${record.id}`;
  // Parsed, not Number()'d: an admin typing "22,50" would otherwise send NaN, which JSON-encodes
  // as null and returns an opaque 422. null here also disables the button rather than letting a
  // blank or malformed amount be submitted. Same parser the reports modal uses.
  const edited = parsePriceInput(amount);

  return (
    <section className="space-y-2">
      <p className="text-sm font-medium">Starting price</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-32"
          disabled={!isOpen || Boolean(price.accepted_at)}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <span className="text-sm text-muted-foreground">{price.currency}</span>
        {/* A source said admission is free: 0 is the price, and accepting it says so on the event. */}
        {price.price === 0 && <span className="text-xs text-muted-foreground">free admission</span>}
        <Confidence value={price.confidence} />
        <ResearchedBy value={price.researched_by} />
        {price.research_stage && <span className="text-xs text-muted-foreground">{price.research_stage}</span>}
        {price.accepted_at ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" /> Saved
            {price.auto_accepted && <AutoAcceptedBadge />}
          </span>
        ) : (
          isOpen && (
            <ActionButton
              busy={busy}
              actionKey={key}
              disabled={edited === null}
              onClick={() =>
                act(key, () => eventResearchApi.acceptPrice(record.id, edited, price.currency), "Price saved to the event")
              }
            >
              Accept price
            </ActionButton>
          )
        )}
      </div>
      {price.notes && <p className="text-xs text-muted-foreground">{price.notes}</p>}
    </section>
  );
}

function RecordCard({ record, busy, setBusy, act: pageAct, onImageSettled, onError }) {
  // Every action on this card refreshes this record alone rather than re-querying the page — see
  // `act`. Bound once here so no call site can forget and reshuffle the list.
  const act = (key, run, message) =>
    pageAct(key, run, message, { recordId: record.id, entityId: record.event_id });
  const isOpen = record.status === "open";
  const candidates = record.ticket_candidates || [];
  // Split rather than filter: the weak ones stay reachable behind a disclosure.
  // An unscored candidate counts as unsure, matching how the record ranks it. build_ticket_candidates
  // sorts one last on the grounds that no score is not the same as a good score, and the only
  // candidate that ends up unscored is the event page the agent read but did not itself put
  // forward as a seller — so treating it as trustworthy here contradicted the ordering it was
  // given. Its own "the page the agent read" note is still shown, inside the disclosure.
  const isConfident = (c) => typeof c.confidence === "number" && c.confidence >= CANDIDATE_CONFIDENCE_FLOOR;
  const artists = record.artists || [];
  // The line-up the event already carries. The proposals above are what it does NOT carry, so
  // this is the only place the card says who is already on it — and the only place a billing
  // position can be corrected once written.
  const lineup = record.event_artists || [];
  const price = record.price;
  // What the event already carries. While the record is open, the "On the event" list below lists
  // these in full with a Remove on each, so a candidate matching one is dropped from the proposals
  // rather than shown twice — the proposal list is then exactly "what is left to decide".
  //
  // On a CLOSED record there is no such list (nothing is actionable), so the candidates stay put
  // and keep their Added / On the event labels. Hiding them there would show only the ones nobody
  // took, which reads as a pass that found nothing.
  //
  // A per-date link only counts as "carried" once it is on EVERY showtime offered. Counting one
  // was wrong in the direction that matters: filing a candidate against the 30th removed its row,
  // so the same page could never be filed against the 1st — the admin had to retype the URL into
  // the manual box to finish the job the picker had just started.
  const showtimes = record.event_occurrences || [];
  const onEveryShowtime = (url) =>
    showtimes.length > 0 && showtimes.every((occurrence) => (occurrence.urls || []).includes(url));
  const eventUrls = new Set(record.event_urls || []);
  const isCarried = (url) => eventUrls.has(url) || onEveryShowtime(url);
  const existing = new Set(candidates.filter((c) => isCarried(c.url)).map((c) => c.url));
  const undecided = isOpen ? candidates.filter((c) => !existing.has(c.url)) : candidates;
  const confident = undecided.filter(isConfident);
  const unsure = undecided.filter((c) => !isConfident(c));

  // The image picked in the alert section below lives here, not in the section, because closing
  // the record has to be able to commit it. Held there, a picked image was silently discarded by
  // "Done": it never reached the event, and the alert stayed open — now orphaned back to the full
  // image queue, since the card that offered to settle it had just left the open list.
  const [pendingImages, setPendingImages] = useState(null);

  // An image the server adds while a pick is pending — a provider image rule, on a URL accepted on
  // this card — joins the pick, after the admin's own choices: saving the pick replaces the event's
  // images, so it would otherwise take the new one straight back off.
  const knownImageUrls = useRef(null);
  useEffect(() => {
    const now = normalizeImageRefs(record.event_images || []);
    const known = knownImageUrls.current;
    knownImageUrls.current = new Set(now.map((image) => image.url));
    if (known === null) return;
    const added = now.filter((image) => !known.has(image.url));
    if (added.length === 0) return;
    setPendingImages((pick) =>
      pick === null ? pick : [...pick, ...added.filter((image) => !pick.some((kept) => kept.url === image.url))]
    );
  }, [record.event_images]);

  // Hoisting the pick above the alert section gave it a lifetime longer than the alert, and
  // `RecordCard` is keyed by record id so it survives every silent reload. That let a pick outlive
  // the thing it was for: choose an image, click "Dismiss alert" — which means *this image is
  // acceptable, do not blocklist it* — and the section unmounts while the pick stays here. Closing
  // the record then wrote those images and called `resolve`, which only refuses an already
  // resolved row, so the dismissed alert was resolved and its flagged URLs blocklisted: the exact
  // decision the admin had just declined to make.
  useEffect(() => {
    if (!record.has_open_image_review) setPendingImages(null);
  }, [record.has_open_image_review]);

  // Save the picked images to the event, then resolve the alert — that order, same as
  // ImageReviewView: with the review row closed first, a failed save would leave Elasticsearch
  // serving the placeholder and no alert left to find it.
  //
  // A no-op when nothing was picked, and deliberately so: closing a record must never resolve an
  // alert nobody acted on. "The admin looked at the placeholder and moved on" is not the same
  // fact as "the admin replaced it", and only the second one has been fixed.
  const commitPendingImages = async () => {
    if (pendingImages === null) return;
    await eventsApi.update(record.event_id, {
      images: pendingImages,
      primary_image_url: pendingImages[0]?.url || null
    });
    await imageReviewApi.resolve(record.event_id);
    setPendingImages(null);
  };

  // Closing runs the image commit first: if the status write then fails the image is still saved
  // and the alert still resolved, which is the harmless half. The other order could close the
  // record and lose the image with no card left to retry from.
  const closeRecord = (status, message) =>
    act(
      `${status}:${record.id}`,
      async () => {
        await commitPendingImages();
        await eventResearchApi.setStatus(record.id, status);
      },
      pendingImages === null ? message : `${message} — image saved and its alert resolved`
    );

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center gap-2">
          <EntityLink type="event" id={record.event_id} className="text-base font-medium text-primary">
            {record.event_title || record.event_id}
          </EntityLink>
          {record.event_venue_name && (
            <span className="text-sm text-muted-foreground">@ {record.event_venue_name}</span>
          )}
          <EventStatusBadge status={record.event_status} />
          {!isOpen && <Badge variant="secondary">{record.status}</Badge>}
          <a
            href={getEventWebUrl(record.event_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            title="Open on the consumer site"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        {/* Rendered while the record is open even with no candidates at all — the manual box below
            is exactly what an empty sweep needs, and hiding the section would hide the fix too. */}
        {(candidates.length > 0 || isOpen) && (
          <section className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Ticket className="h-4 w-4" /> Where it is on sale ({confident.length})
            </p>
            {confident.map((candidate) => (
              <Candidate
                key={candidate.url}
                candidate={candidate}
                record={record}
                busy={busy}
                act={act}
                isOpen={isOpen}
                onEvent={existing.has(candidate.url)}
              />
            ))}
            {unsure.length > 0 && (
              <details className="text-xs">
                <summary className="w-fit cursor-pointer text-muted-foreground hover:text-foreground">
                  {unsure.length} the agent was not sure about
                </summary>
                {/* Kept, not dropped: a link it could not open is still a lead, and you asked for
                    as many sellers as possible. Just not offered as an equal — these are where
                    the wrong-year and wrong-act links turn up. */}
                <div className="mt-2 space-y-2">
                  {unsure.map((candidate) => (
                    <Candidate
                      key={candidate.url}
                      candidate={candidate}
                      record={record}
                      busy={busy}
                      act={act}
                      isOpen={isOpen}
                      onEvent={existing.has(candidate.url)}
                    />
                  ))}
                </div>
              </details>
            )}
            {isOpen && <ManualUrl record={record} busy={busy} act={act} />}
          </section>
        )}

        {artists.length === 0 && (
          <AbsentSection icon={Users} title="Missing artists" reason={absentLineupReason(record)} />
        )}

        {artists.length > 0 && (
          <section className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" /> Missing artists ({artists.length})
            </p>
            {artists.map((artist) => (
              <ProposedArtist
                key={artist.name}
                artist={artist}
                record={record}
                busy={busy}
                setBusy={setBusy}
                act={act}
                isOpen={isOpen}
                onError={onError}
              />
            ))}
          </section>
        )}

        {lineup.length > 0 && (
          <section className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" /> Already on the event ({lineup.length})
            </p>
            {/* Keyed on the stored rank as well as the id: `EventArtist` seeds its box from the
                prop once, and every action on this card re-reads the record — so without the
                remount a saved rank would keep showing whatever was in the box before, and a
                change made elsewhere would never appear. Same trick as the price/description
                proposals below, and it leaves an in-progress edit alone because only a
                server-side change moves the key. */}
            {lineup.map((artist) => (
              <EventArtist
                key={`${artist.id}:${artist.performance_rank ?? ""}`}
                artist={artist}
                record={record}
                busy={busy}
                act={act}
                isOpen={isOpen}
              />
            ))}
          </section>
        )}

        {/* No proposal to accept. That is not the same as nothing to do: while the record is open
            and the event is still unpriced, the agent's reasoning is often enough for a human to
            settle it — so the box is offered instead of a dead explanation. */}
        {!price &&
          (isOpen && record.event_start_price?.price == null ? (
            <ManualPrice record={record} busy={busy} act={act} />
          ) : (
            <AbsentSection icon={Euro} title="Starting price" reason={absentPriceReason(record)} />
          ))}

        {/* `key` is what makes both of these resync. Their edit lives in component state, and a
            later agent pass can replace a proposal nobody has accepted yet — without the remount
            the box keeps showing the superseded wording and accepting writes it back over the
            newer one. Keying on the *proposed value* leaves an admin's in-progress edit alone,
            since only a server-side change moves the key. Deliberately not on `accepted_at`:
            accept-price stamps that without storing the corrected amount, so remounting there
            would replace what the admin actually saved with the original proposal on screen.
            accept-description does store the accepted wording, so its key follows it. */}
        {price && (
          <ProposedPrice
            key={`price:${price.price}`}
            record={record}
            price={price}
            busy={busy}
            act={act}
            isOpen={isOpen}
          />
        )}

        {record.cancelled && <ProposedCancellation record={record} busy={busy} act={act} isOpen={isOpen} />}

        {/* Keyed on the proposed title, which accept-title stores as accepted, like the description. */}
        {record.title ? (
          <ProposedTitle
            key={`title:${record.title.title || ""}`}
            record={record}
            busy={busy}
            act={act}
            isOpen={isOpen}
          />
        ) : (
          <AbsentSection icon={Heading} title="Title" reason={absentTitleReason(record)} />
        )}

        {record.description ? (
          <ProposedDescription
            key={`description:${record.description.text || ""}`}
            record={record}
            busy={busy}
            act={act}
            isOpen={isOpen}
          />
        ) : (
          <AbsentSection icon={FileText} title="Description" reason={absentDescriptionReason(record)} />
        )}

        {/* Keyed on the proposed value for the same reason as the price above. */}
        {STATED_VALUES.map((spec) =>
          record[spec.ask] ? (
            <ProposedStatedValue
              key={`${spec.ask}:${record[spec.ask][spec.key]}`}
              record={record}
              spec={spec}
              busy={busy}
              act={act}
              isOpen={isOpen}
            />
          ) : (
            <AbsentSection
              key={spec.ask}
              icon={spec.icon}
              title={spec.title}
              reason={absentStatedValueReason(record, spec)}
            />
          )
        )}

        {TIME_ITEMS.map((spec) =>
          record[spec.ask] ? (
            <ProposedTimes key={spec.ask} record={record} spec={spec} busy={busy} act={act} isOpen={isOpen} />
          ) : (
            <AbsentSection key={spec.ask} icon={spec.icon} title={spec.title} reason={absentTimesReason(record, spec)} />
          )
        )}

        {record.has_open_image_review && (
          <ImageAlertSection
            record={record}
            busy={busy}
            setBusy={setBusy}
            pendingImages={pendingImages}
            setPendingImages={setPendingImages}
            commit={commitPendingImages}
            onSettled={(message) => onImageSettled(record.id, message)}
            onError={onError}
          />
        )}

        {isOpen && (
          <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
            <Button
              size="sm"
              variant="ghost"
              disabled={Boolean(busy)}
              onClick={() => closeRecord("dismissed", "Record dismissed")}
            >
              <X className="mr-1 h-4 w-4" /> Dismiss
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={Boolean(busy)}
              onClick={() => closeRecord("resolved", "Record closed")}
            >
              <Check className="mr-1 h-4 w-4" /> Done
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
